import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import Select from "react-select";
import DatePicker from "react-datepicker";
import { LuSlidersHorizontal } from "react-icons/lu";
import { fetchBlogAuthors, fetchBlogPosts, searchBlogPosts } from "../../utils/apiUtils";
import MarkdownRenderer from "../markdown/MarkdownRenderer";
import {
    AvatarMark,
    BLOG_SEARCH_FROM_DEFAULT,
    Stars,
    displayName,
    formatRelative,
    parseBlogSearchRange,
    readBlogSearch,
    stampToDate,
    todayStamp,
    writeBlogSearch,
} from "./blogShared.jsx";
import { resetBlogTour, startFeedTour } from "./blogWalkthrough.js";
import { useBlogWalkthrough } from "./useBlogWalkthrough.js";
import "./Blog.scss";

const PAGE = 8;

const CalendarHeader = ({
    monthDate,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
}) => (
    <div className="blog-datepicker__head">
        <button
            type="button"
            className="blog-datepicker__nav"
            onClick={decreaseMonth}
            disabled={prevMonthButtonDisabled}
            aria-label="Previous month"
        >
            ‹
        </button>
        <p className="blog-datepicker__month">
            {monthDate.toLocaleString("en-US", { month: "long", year: "numeric" })}
        </p>
        <button
            type="button"
            className="blog-datepicker__nav"
            onClick={increaseMonth}
            disabled={nextMonthButtonDisabled}
            aria-label="Next month"
        >
            ›
        </button>
    </div>
);

const RangeDate = ({ label, value, min, max, onChange }) => {
    const labelId = `blog-range-${label.toLowerCase()}`;
    return (
        <div className="blog__range">
            <span id={labelId}>{label}</span>
            <DatePicker
                selected={stampToDate(value)}
                onChange={(date) => onChange(date ? todayStamp(date) : "")}
                minDate={stampToDate(min)}
                maxDate={stampToDate(max)}
                dateFormat="MMM d, yyyy"
                placeholderText={label}
                className="date-picker blog__date"
                wrapperClassName="blog__date-wrap"
                calendarClassName="blog-datepicker"
                popperClassName="blog-date-popper"
                portalId="root"
                popperPlacement="bottom-start"
                popperProps={{ strategy: "fixed" }}
                showPopperArrow={false}
                formatWeekDay={(day) => day.slice(0, 2)}
                renderCustomHeader={CalendarHeader}
                ariaLabelledBy={labelId}
            />
        </div>
    );
};

const PostCard = ({ post, onAuthor }) => {
    const excerpt = post.excerpt || "";
    const long = excerpt.length > 140;
    return (
        <li>
            <Link to={`/blog/${post.id}`} className="blog__card">
                <div className="blog__who">
                    <AvatarMark email={post.authorEmail} />
                    <div>
                        <strong
                            className="blog__name blog__name--filter"
                            onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                onAuthor?.(post.authorEmail);
                            }}
                        >
                            {displayName(post.authorEmail)}
                        </strong>
                        <em>{formatRelative(post.createdAt)}</em>
                    </div>
                </div>
                <Stars value={post.ratingAvg} readOnly />
                <p className="blog__title">{post.title}</p>
                <p className="blog__excerpt">
                    {long ? `${excerpt.slice(0, 140).trim()}…` : excerpt}
                    {long ? <span className="blog__more"> See more</span> : null}
                </p>
            </Link>
        </li>
    );
};

const Blogs = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const initialSearch = searchParams.get("q") || readBlogSearch();
    const authorFilter = searchParams.get("author") || "";
    const initialRange = parseBlogSearchRange({
        from: searchParams.get("from"),
        to: searchParams.get("to"),
    });
    const [searchValue, setSearchValue] = useState(initialSearch);
    const [submittedSearch, setSubmittedSearch] = useState(initialSearch.trim());
    const [fromDay, setFromDay] = useState(initialRange.from);
    const [toDay, setToDay] = useState(initialRange.to);
    const [filtersOpen, setFiltersOpen] = useState(
        () => Boolean(authorFilter)
            || initialRange.from !== BLOG_SEARCH_FROM_DEFAULT
            || initialRange.to !== todayStamp(),
    );
    const [scrollRoot, setScrollRoot] = useState(null);
    const { ref, inView } = useInView({
        root: scrollRoot,
        rootMargin: "160px",
        threshold: 0,
    });

    const writeParams = (next) => {
        const params = {};
        if (next.q) params.q = next.q;
        if (next.author) params.author = next.author;
        if (next.from) params.from = next.from;
        if (next.to) params.to = next.to;
        setSearchParams(params, { replace: true });
    };

    const runSearch = (value, range = { from: fromDay, to: toDay }) => {
        const q = String(value || "").trim();
        const next = parseBlogSearchRange(range);
        setFromDay(next.from);
        setToDay(next.to);
        setSubmittedSearch(q);
        writeBlogSearch(q);
        writeParams({ q, author: q ? "" : authorFilter, from: next.from, to: next.to });
    };

    const runAuthor = (email) => {
        const author = String(email || "").trim();
        setSearchValue("");
        setSubmittedSearch("");
        writeBlogSearch("");
        setFiltersOpen(true);
        writeParams({ author, from: fromDay, to: toDay });
    };

    const resetFilters = () => {
        const from = BLOG_SEARCH_FROM_DEFAULT;
        const to = todayStamp();
        setFromDay(from);
        setToDay(to);
        if (submittedSearch) {
            runSearch(submittedSearch, { from, to });
            return;
        }
        writeParams({ from, to });
    };

    useEffect(() => {
        if (initialSearch.trim() && !searchParams.get("q") && !searchParams.get("author")) {
            setSearchParams({
                q: initialSearch.trim(),
                from: fromDay,
                to: toDay,
            }, { replace: true });
        }
    }, []);

    const searching = Boolean(submittedSearch);

    const authors = useQuery({
        queryKey: ["blog-authors"],
        queryFn: fetchBlogAuthors,
        staleTime: 5 * 60 * 1000,
    });

    const feed = useInfiniteQuery({
        queryKey: ["blog-feed", PAGE, authorFilter],
        queryFn: async ({ pageParam }) => fetchBlogPosts({
            cursor: pageParam,
            limit: PAGE,
            author: authorFilter || undefined,
        }),
        initialPageParam: undefined,
        getNextPageParam: (lastPage) => lastPage?.nextCursor || undefined,
        enabled: !searching,
    });

    const search = useQuery({
        queryKey: ["blog-search", submittedSearch, fromDay, toDay],
        queryFn: () => searchBlogPosts(submittedSearch, { from: fromDay, to: toDay }),
        enabled: searching,
        staleTime: 5 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
    });

    const fetchMoreRef = useRef(feed.fetchNextPage);
    const canFetchRef = useRef(false);
    fetchMoreRef.current = feed.fetchNextPage;
    canFetchRef.current = !searching && Boolean(feed.hasNextPage) && !feed.isFetchingNextPage;

    useEffect(() => {
        if (inView && canFetchRef.current) {
            fetchMoreRef.current();
        }
    }, [inView]);

    useEffect(() => {
        if (!scrollRoot) return undefined;
        const onScroll = () => {
            const remain = scrollRoot.scrollHeight - scrollRoot.scrollTop - scrollRoot.clientHeight;
            if (remain < 180 && canFetchRef.current) {
                fetchMoreRef.current();
            }
        };
        scrollRoot.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => scrollRoot.removeEventListener("scroll", onScroll);
    }, [scrollRoot]);

    const rows = (searching
        ? search.data?.rows || []
        : (feed.data?.pages || []).flatMap((page) => page.rows || [])
    ).slice().sort((a, b) => String(b.createdAt || "").localeCompare(String(a.createdAt || "")));

    const pending = searching ? search.isPending : feed.isPending;
    const error = searching ? search.error : feed.error;

    const authorOptions = useMemo(() => {
        const rows = authors.data?.rows || [];
        const listed = rows.some((row) => row.authorEmail === authorFilter);
        const extra = authorFilter && !listed ? [{ authorEmail: authorFilter }] : [];
        return [
            { value: "", label: "Everyone" },
            ...[...rows, ...extra].map((row) => ({
                value: row.authorEmail,
                label: displayName(row.authorEmail),
                email: row.authorEmail,
            })),
        ];
    }, [authors.data, authorFilter]);

    const authorValue = authorOptions.find((option) => option.value === authorFilter) || authorOptions[0];
    const filtersActive = Boolean(authorFilter)
        || fromDay !== BLOG_SEARCH_FROM_DEFAULT
        || toDay !== todayStamp();
    const goWrite = () => navigate("/blog/new");
    useBlogWalkthrough("feed", { onWrite: goWrite });

    return (
        <div className="container blog blog--feed scrollable" ref={setScrollRoot}>
            <div className="blog__heading">
                <p className="title" data-blog-tour="welcome">Write. Connect. Inspire.</p>
                <div className="blog__heading-actions">
                    <button
                        type="button"
                        className="blog__btn blog__btn--text"
                        onClick={() => {
                            resetBlogTour();
                            startFeedTour({ onWrite: goWrite });
                        }}
                    >
                        How this works
                    </button>
                    <Link to="/blog/new" className="blog__btn blog__btn--primary" data-blog-tour="write">
                        Write a post
                    </Link>
                </div>
            </div>
            <p className="sub-title">
                Notes for the whole team. Search by meaning — try “employer cash call” or “in-network”.
            </p>

            <div className="blog__toolbar">
                <form
                    id="blog-search-form"
                    className="blog__search-form"
                    data-blog-tour="search"
                    onSubmit={(event) => {
                        event.preventDefault();
                        runSearch(searchValue);
                    }}
                >
                    <div className="blog__search-row">
                        <input
                            className="blog__search"
                            type="search"
                            placeholder="Search with AI"
                            value={searchValue}
                            onChange={(event) => {
                                const value = event.target.value;
                                setSearchValue(value);
                                if (!value.trim()) runSearch("");
                            }}
                        />
                        <button
                            type="button"
                            className={`blog__filter-toggle${filtersOpen ? " is-on" : ""}${filtersActive ? " has-filters" : ""}`}
                            onClick={() => setFiltersOpen((open) => !open)}
                            aria-pressed={filtersOpen}
                            aria-expanded={filtersOpen}
                            aria-controls="blog-search-filters"
                            data-blog-tour="from"
                            aria-label={
                                filtersOpen
                                    ? "Hide search filters"
                                    : filtersActive
                                        ? "Show search filters, filters applied"
                                        : "Show search filters"
                            }
                            title={filtersOpen ? "Hide filters" : filtersActive ? "Filters applied" : "Filters"}
                        >
                            <LuSlidersHorizontal size="1.15rem" />
                            {filtersActive ? <span className="blog__filter-dot" aria-hidden /> : null}
                        </button>
                        <button type="submit" className="blog__btn blog__btn--ghost">
                            Search
                        </button>
                    </div>
                    {filtersOpen ? (
                    <div className="blog__filters" id="blog-search-filters">
                        <RangeDate
                            label="Since"
                            value={fromDay}
                            max={toDay}
                            onChange={(from) => {
                                setFromDay(from);
                                if (submittedSearch) runSearch(submittedSearch, { from, to: toDay });
                            }}
                        />
                        <RangeDate
                            label="Until"
                            value={toDay}
                            min={fromDay}
                            onChange={(to) => {
                                setToDay(to);
                                if (submittedSearch) runSearch(submittedSearch, { from: fromDay, to });
                            }}
                        />
                        <label className="blog__author-filter">
                            <span>From</span>
                            <Select
                                className="react-select-container"
                                classNamePrefix="react-select"
                                placeholder="Everyone"
                                options={authorOptions}
                                value={authorValue}
                                onChange={(option) => runAuthor(option?.value || "")}
                                isClearable={Boolean(authorFilter)}
                                isSearchable
                                filterOption={(option, input) => {
                                    const q = String(input || "").trim().toLowerCase();
                                    if (!q) return true;
                                    return `${option.label} ${option.value}`.toLowerCase().includes(q);
                                }}
                                formatOptionLabel={(option, { context }) => (
                                    option.value ? (
                                        <span className="blog__author-opt">
                                            {context === "menu" ? <AvatarMark email={option.email} /> : null}
                                            <span>{option.label}</span>
                                        </span>
                                    ) : option.label
                                )}
                                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                menuPosition="fixed"
                                styles={{
                                    menuPortal: (base) => ({ ...base, zIndex: 20 }),
                                    menu: (base) => ({
                                        ...base,
                                        borderRadius: "0.4rem",
                                        overflow: "hidden",
                                        border: "1px solid var(--lighterGrey, #e0e0e0)",
                                        boxShadow: "0 4px 16px rgba(17, 17, 26, 0.08)",
                                    }),
                                    option: (base, state) => ({
                                        ...base,
                                        fontSize: "0.88rem",
                                        fontWeight: 600,
                                        color: "var(--darkerGrey, #333)",
                                        backgroundColor: state.isSelected
                                            ? "#eef6fa"
                                            : state.isFocused
                                                ? "#f6fafc"
                                                : "#fff",
                                    }),
                                }}
                            />
                        </label>
                        <button
                            type="button"
                            className="blog__btn blog__btn--ghost"
                            onClick={resetFilters}
                            disabled={!filtersActive}
                        >
                            Reset
                        </button>
                    </div>
                    ) : null}
                </form>
            </div>

            {error ? <p className="blog__error">{error.message}</p> : null}
            {searching && search.isPending && !search.data?.answer ? (
                <div className="blog__ai">
                    <p className="blog__ai-label">AI</p>
                    <p className="blog__ai-status">Looking through posts…</p>
                </div>
            ) : null}
            {searching && search.data?.answer ? (
                <div className="blog__ai">
                    <p className="blog__ai-label">AI</p>
                    <div className="blog__ai-body">
                        <MarkdownRenderer>{search.data.answer}</MarkdownRenderer>
                    </div>
                </div>
            ) : null}
            {pending && !searching ? <p className="blog__status">Loading…</p> : null}
            {!pending && !rows.length ? (
                <p className="blog__empty">
                    {searching
                        ? "No posts match that search."
                        : authorFilter
                            ? `No posts from ${displayName(authorFilter)} yet.`
                            : "No posts yet."}
                </p>
            ) : null}

            <ul className="blog__list">
                {rows.map((post) => (
                    <PostCard key={post.id} post={post} onAuthor={runAuthor} />
                ))}
            </ul>

            {!searching ? (
                <div
                    ref={ref}
                    className="blog__sentinel"
                    data-has-next={feed.hasNextPage ? "1" : "0"}
                    data-fetching={feed.isFetchingNextPage ? "1" : "0"}
                />
            ) : null}
            {feed.isFetchingNextPage ? <p className="blog__status">Loading more…</p> : null}
            {!searching && feed.hasNextPage && !feed.isFetchingNextPage ? (
                <button
                    type="button"
                    className="blog__more-link"
                    onClick={() => feed.fetchNextPage()}
                >
                    Load more
                </button>
            ) : null}
        </div>
    );
};

export default Blogs;
